import { forwardRef } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";
import { getCharIcon } from "../../utils/characteristicIcons";
import {
  characteristicLabel,
  isAffirmative,
  isBooleanCharacteristic,
  isNumericCharacteristic,
} from "../../utils/characteristicLabel";

// Cartel tamaño A4 real, en mm: foto a todo el ancho arriba (sin márgenes),
// divisor, y abajo nombre, dirección, precio y los datos elegidos como
// insignias con ícono (no texto plano), con el QR siempre anclado a la
// esquina inferior derecha de la hoja. Se renderiza siempre a tamaño
// completo; la página lo muestra escalado con CSS para la vista previa, y
// la descarga/impresión lo usan tal cual para obtener buena resolución.
const CartelCard = forwardRef(function CartelCard(
  { property, image, selectedCharacteristics, qrUrl, id },
  ref
) {
  // Solo se muestran amenities afirmativas (p.ej. "Con patio"); "Sin X" no
  // suma como insignia visual, así que se descarta en vez de mostrarse feo.
  const badges = selectedCharacteristics.filter(
    ({ key, value }) =>
      isNumericCharacteristic(key) || (isBooleanCharacteristic(key) && isAffirmative(value))
  );

  return (
    <div
      ref={ref}
      id={id}
      style={{ width: "210mm", minHeight: "297mm" }}
      className="bg-white text-gray-800 flex flex-col overflow-hidden"
    >
      {/* Foto a todo el ancho, de borde a borde */}
      <div className="relative w-full flex-shrink-0" style={{ height: "165mm" }}>
        {image ? (
          <img
            src={image}
            alt={property.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center text-gray-400">
            Sin imagen
          </div>
        )}
        {property.state && (
          <span className="absolute top-6 left-6 bg-primary text-white text-xs font-bold uppercase tracking-[0.18em] px-4 py-2 rounded-full shadow-lg">
            {property.state}
          </span>
        )}
      </div>

      {/* Ficha */}
      <div className="flex-1 flex flex-col px-10 py-8 border-t border-gray-200">
        {(property.type || property.state) && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary mb-2">
            {[property.type, property.state].filter(Boolean).join("  ·  ")}
          </p>
        )}
        <h1 className="text-[2.1rem] font-black text-gray-900 capitalize leading-tight tracking-tight">
          {property.name}
        </h1>
        <div className="w-10 h-[3px] bg-primary rounded-full mt-3 mb-3" />
        {(property.address || property.country) && (
          <p className="flex items-center gap-1.5 text-gray-500 text-sm">
            <FaMapMarkerAlt className="text-primary flex-shrink-0 w-3.5 h-3.5" />
            {[property.address, property.country].filter(Boolean).join(", ")}
          </p>
        )}
        {property.currency && property.currency !== "Consultar" && property.price && (
          <p className="text-2xl font-extrabold text-gray-900 leading-none mt-4">
            <span className="text-sm font-bold text-primary align-top mr-1.5">
              {property.currency}
            </span>
            {Number(property.price).toLocaleString("es-AR")}
          </p>
        )}

        {/* Datos (insignias con ícono) + QR, en la misma fila */}
        <div className="flex items-center justify-between gap-6 mt-7">
          {badges.length > 0 && (
            <div className="grid grid-cols-2 gap-x-8 gap-y-8 flex-1">
              {badges.map(({ key, value }) => {
                const Icon = getCharIcon(key);
                const numeric = isNumericCharacteristic(key);
                return (
                  <div key={key} className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    {numeric ? (
                      <div className="leading-tight">
                        <p className="text-2xl font-bold text-gray-900">{value}</p>
                        <p className="text-xs text-gray-400 uppercase tracking-wide">
                          {characteristicLabel(key)}
                        </p>
                      </div>
                    ) : (
                      <p className="text-lg font-semibold text-gray-700">
                        {characteristicLabel(key)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="text-center flex-shrink-0">
            <div className="p-4 bg-primary/5 border-2 border-primary/15 rounded-2xl">
              <QRCodeSVG value={qrUrl} size={160} fgColor="#1f2937" />
            </div>
            <p className="text-xs font-semibold text-primary uppercase tracking-wide mt-2.5">
              Escaneá
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

export default CartelCard;
